import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  FileText,
  Users,
  Ticket,
  Mail,
  Phone,
  Calendar,
  Trash2,
  UserPlus,
  KeyRound,
  Loader2,
  Download,
  Building2,
} from "lucide-react";
import { useAuth, authHeader, API, formatApiError } from "@/lib/auth";
import { COMPANY } from "@/lib/company";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function AdminDashboardPage() {
  const { admin, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!admin) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, admin]);

  if (!loading && !admin) return <Navigate to="/admin/login" replace />;

  const fetchAll = async () => {
    setBusy(true);
    try {
      const [s, v, a] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { headers: authHeader() }),
        axios.get(`${API}/admin/voucher-captures`, { headers: authHeader() }),
        axios.get(`${API}/admin/admins`, { headers: authHeader() }),
      ]);
      setSubmissions(s.data.items || []);
      setVouchers(v.data.items || []);
      setAdmins(a.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm("Delete this admin?")) return;
    try {
      await axios.delete(`${API}/admin/admins/${adminId}`, { headers: authHeader() });
      toast.success("Admin removed");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const exportCsv = (rows, filename) => {
    if (!rows.length) return toast.info("No data to export");
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading admin session...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]" data-testid="admin-dashboard">
      <header className="bg-[#0B1221] border-b border-slate-800 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img
              src={COMPANY.logoUrl}
              onError={(e) => { e.currentTarget.src = COMPANY.logoFallback; }}
              alt="A.I.O"
              className="h-10 w-10 object-contain"
            />
            <div>
              <div className="font-display font-extrabold text-white text-base tracking-tight leading-tight">
                A.I.O Admin Desk
              </div>
              <div className="text-red-500 text-[10px] tracking-[0.25em] font-bold uppercase">
                {admin.email}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-change-password-btn"
              onClick={() => setShowPassword(true)}
              className="aios-btn-secondary text-xs px-4 py-2"
            >
              <KeyRound className="w-4 h-4" /> Change Password
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="aios-btn-primary text-xs px-4 py-2"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <StatCard icon={FileText} label="Sourcing Requests" value={submissions.length} testId="stat-submissions" />
          <StatCard icon={Ticket} label="Voucher / Portal Captures" value={vouchers.length} testId="stat-vouchers" />
          <StatCard icon={Users} label="Admin Users" value={admins.length} testId="stat-admins" />
        </div>

        <Tabs defaultValue="submissions">
          <TabsList className="bg-[#0B1221] border border-slate-800 p-1">
            <TabsTrigger value="submissions" data-testid="tab-submissions" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              Sourcing Requests
            </TabsTrigger>
            <TabsTrigger value="vouchers" data-testid="tab-vouchers" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              Voucher Captures
            </TabsTrigger>
            <TabsTrigger value="admins" data-testid="tab-admins" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              Admins
            </TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="mt-6">
            <SectionHeader
              title="Sourcing Requests"
              subtitle={`${submissions.length} total submissions`}
              onExport={() => exportCsv(submissions, "sourcing_requests.csv")}
            />
            {busy ? <Loading /> : submissions.length === 0 ? <Empty label="No submissions yet" /> : (
              <div className="space-y-3" data-testid="submissions-list">
                {submissions.map((s) => (
                  <div key={s.id} className="aios-card p-6" data-testid={`submission-${s.id}`}>
                    <div className="aios-card-accent" />
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="font-display font-bold text-white text-lg">{s.full_name}</div>
                        <div className="flex items-center gap-2 text-slate-400 text-sm mt-1">
                          <Building2 className="w-3.5 h-3.5" /> {s.company_name}
                        </div>
                      </div>
                      <div className="text-right text-xs text-slate-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(s.created_at).toLocaleString()}
                      </div>
                    </div>
                    <p className="mt-4 text-slate-300 leading-relaxed text-sm whitespace-pre-wrap">{s.sourcing_needs}</p>
                    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-400">
                      {s.email && <a href={`mailto:${s.email}`} className="flex items-center gap-1 hover:text-red-500"><Mail className="w-3 h-3" /> {s.email}</a>}
                      {s.phone && <a href={`tel:${s.phone}`} className="flex items-center gap-1 hover:text-red-500"><Phone className="w-3 h-3" /> {s.phone}</a>}
                      {s.invoice_filename && <span className="flex items-center gap-1"><FileText className="w-3 h-3 text-red-500" /> {s.invoice_filename}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="vouchers" className="mt-6">
            <SectionHeader
              title="Voucher / Portal Captures"
              subtitle="Emails captured from post-purchase landing pages"
              onExport={() => exportCsv(vouchers, "voucher_captures.csv")}
            />
            {busy ? <Loading /> : vouchers.length === 0 ? <Empty label="No captures yet" /> : (
              <div className="aios-card overflow-hidden" data-testid="vouchers-table">
                <div className="aios-card-accent" />
                <table className="w-full text-sm">
                  <thead className="bg-[#0B1221] text-slate-400 font-display tracking-widest text-xs uppercase">
                    <tr>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3">Name</th>
                      <th className="text-left px-5 py-3">Product</th>
                      <th className="text-left px-5 py-3">Captured</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    {vouchers.map((v) => (
                      <tr key={v.id} className="border-t border-slate-800">
                        <td className="px-5 py-3">{v.email}</td>
                        <td className="px-5 py-3">{v.full_name || "—"}</td>
                        <td className="px-5 py-3">
                          <span className={`font-display text-xs tracking-widest font-bold uppercase ${v.product === "portal_485" ? "text-red-500" : "text-amber-400"}`}>
                            {v.product === "portal_485" ? "Portal $485" : "Test Drive $9.95"}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-slate-500 text-xs">{new Date(v.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="admins" className="mt-6">
            <SectionHeader
              title="Admin Users"
              subtitle="Multi-user admin access management"
              action={
                <button
                  onClick={() => setShowCreate(true)}
                  data-testid="admin-create-btn"
                  className="aios-btn-primary text-xs px-4 py-2"
                >
                  <UserPlus className="w-4 h-4" /> New Admin
                </button>
              }
            />
            {busy ? <Loading /> : (
              <div className="aios-card overflow-hidden" data-testid="admins-table">
                <div className="aios-card-accent" />
                <table className="w-full text-sm">
                  <thead className="bg-[#0B1221] text-slate-400 font-display tracking-widest text-xs uppercase">
                    <tr>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3">Name</th>
                      <th className="text-left px-5 py-3">Created</th>
                      <th className="text-right px-5 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    {admins.map((a) => (
                      <tr key={a.id} className="border-t border-slate-800">
                        <td className="px-5 py-3 font-medium text-white">{a.email}</td>
                        <td className="px-5 py-3">{a.name}</td>
                        <td className="px-5 py-3 text-slate-500 text-xs">{a.created_at ? new Date(a.created_at).toLocaleString() : "—"}</td>
                        <td className="px-5 py-3 text-right">
                          {a.id !== admin.id && (
                            <button
                              data-testid={`delete-admin-${a.id}`}
                              onClick={() => handleDeleteAdmin(a.id)}
                              className="text-slate-500 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          {a.id === admin.id && <span className="text-xs text-red-500 font-display tracking-widest">YOU</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <CreateAdminDialog open={showCreate} onClose={() => setShowCreate(false)} onSuccess={fetchAll} />
      <ChangePasswordDialog open={showPassword} onClose={() => setShowPassword(false)} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="aios-card p-6" data-testid={testId}>
      <div className="aios-card-accent" />
      <Icon className="w-7 h-7 text-red-500" />
      <div className="font-display text-4xl font-extrabold text-white tracking-tighter mt-4">{value}</div>
      <div className="text-slate-400 text-xs tracking-widest font-display uppercase mt-1">{label}</div>
    </div>
  );
}

function SectionHeader({ title, subtitle, action, onExport }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
      <div>
        <h2 className="font-display text-2xl font-bold text-white tracking-tight">{title}</h2>
        {subtitle && <p className="text-slate-500 text-sm mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">
        {onExport && (
          <button onClick={onExport} className="aios-btn-secondary text-xs px-4 py-2" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        )}
        {action}
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center justify-center py-20 text-slate-500">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="aios-card p-10 text-center text-slate-500" data-testid="empty-state">
      <div className="aios-card-accent" />
      {label}
    </div>
  );
}

function CreateAdminDialog({ open, onClose, onSuccess }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/admin/admins`, { email: email.trim().toLowerCase(), name, password }, { headers: authHeader() });
      toast.success("Admin created");
      setEmail(""); setName(""); setPassword("");
      onClose();
      onSuccess();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0B1221] border-slate-700 text-white" data-testid="create-admin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight text-white">Add Admin User</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <input
            type="email" required placeholder="email@allinonesourcing.com"
            value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="create-admin-email"
            className="w-full bg-[#020617] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100"
          />
          <input
            type="text" placeholder="Name (optional)"
            value={name} onChange={(e) => setName(e.target.value)}
            data-testid="create-admin-name"
            className="w-full bg-[#020617] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100"
          />
          <input
            type="password" required minLength={8} placeholder="Password (min 8 chars)"
            value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="create-admin-password"
            className="w-full bg-[#020617] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100"
          />
          <button type="submit" disabled={busy} data-testid="create-admin-submit" className="aios-btn-primary w-full text-sm">
            {busy ? "Creating..." : "Create Admin"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordDialog({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { headers: authHeader() });
      toast.success("Password updated");
      setCurrent(""); setNext("");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0B1221] border-slate-700 text-white" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight text-white">Change Password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <input
            type="password" required placeholder="Current password"
            value={current} onChange={(e) => setCurrent(e.target.value)}
            data-testid="change-password-current"
            className="w-full bg-[#020617] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100"
          />
          <input
            type="password" required minLength={8} placeholder="New password (min 8 chars)"
            value={next} onChange={(e) => setNext(e.target.value)}
            data-testid="change-password-new"
            className="w-full bg-[#020617] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100"
          />
          <button type="submit" disabled={busy} data-testid="change-password-submit" className="aios-btn-primary w-full text-sm">
            {busy ? "Updating..." : "Update Password"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
